import { useEffect, useMemo } from "react";
import { useQuery } from "react-query";
import useGlobalContext from "hooks/globalData";
import {
  EvaluationFormMode,
  SkuAvailabilityId,
  SkuAvailabilityName,
} from "types/common-types";
import {
  IEvaluation,
  ListResponseOfEvaluationType,
  SettingFilter,
  AdminListRequestOfSettingFilter,
} from "services/KnowfullyAdminClient";

type EvaluationFormValues = Partial<IEvaluation> & {
  questions?: any[];
};

type DefaultOrganizationEvaluationSettingsPopulatorProps = {
  evaluationTypeId?: string;
  skuAvailabilityId?: string;
  organizationId?: string;
  mode: EvaluationFormMode;
  formChange: <F extends keyof EvaluationFormValues>(
    name: F,
    value?: EvaluationFormValues[F]
  ) => void;
  evaluationTypes?: ListResponseOfEvaluationType;
};

const DefaultOrganizationEvaluationSettingsPopulator = ({
  evaluationTypeId,
  skuAvailabilityId,
  organizationId,
  mode,
  formChange,
  evaluationTypes,
}: DefaultOrganizationEvaluationSettingsPopulatorProps) => {
  const { settingService } = useGlobalContext();

  const evaluationTypeName = evaluationTypes?.data?.find(
    (et) => et.id === evaluationTypeId
  )?.name;

  const settingsGroupCodeConstant = useMemo(() => {
    if (!evaluationTypeName) return null;

    if (skuAvailabilityId) {
      const skuAvailabilityName =
        SkuAvailabilityName[skuAvailabilityId as SkuAvailabilityId];

      if (skuAvailabilityName) {
        return `${evaluationTypeName}-${skuAvailabilityName}`;
      }
    }

    return evaluationTypeName;
  }, [evaluationTypeName, skuAvailabilityId]);

  const { data: settingGroups } = useQuery(
    ["settingGroups"],
    () => settingService.getSettingGroups(null),
    {
      enabled: mode === EvaluationFormMode.Create,
    }
  );

  const settingGroupId = useMemo(() => {
    if (!settingsGroupCodeConstant || !settingGroups?.data) return null;

    const group = settingGroups.data.find(
      (g) => (g as any).codeConstant === settingsGroupCodeConstant
    );
    return group?.id || null;
  }, [settingsGroupCodeConstant, settingGroups]);

  const { data: defaultSettingsResponse } = useQuery(
    ["evaluationDefaultSettings", organizationId, settingGroupId],
    () => {
      return settingService.getSettings(
        new AdminListRequestOfSettingFilter({
          parameters: new SettingFilter({
            settingGroupId: settingGroupId!,
          }),
          organizationId: organizationId!,
        })
      );
    },
    {
      enabled:
        !!settingGroupId &&
        !!organizationId &&
        mode === EvaluationFormMode.Create,
      staleTime: 0,
    }
  );

  const settingsDict = useMemo(() => {
    if (!defaultSettingsResponse?.data) return null;

    const dict: Record<string, string> = {};
    defaultSettingsResponse.data.forEach((setting) => {
      if (setting.codeConstant && setting.value !== undefined) {
        dict[setting.codeConstant] = setting.value;
      }
    });

    return dict;
  }, [defaultSettingsResponse]);

  useEffect(() => {
    if (mode !== EvaluationFormMode.Create) return;

    if (settingsDict) {
      const settings = settingsDict;

      const enablePrintRefKey = Object.keys(settings).find((k) =>
        k.includes("EnablePrintReference")
      );
      const confidenceLevelsKey = Object.keys(settings).find((k) =>
        k.includes("AddConfidenceLevels")
      );
      const passingScoreKey = Object.keys(settings).find((k) =>
        k.includes("PassingScore")
      );
      const attemptsAllowedKey = Object.keys(settings).find((k) =>
        k.includes("AttemptsAllowed")
      );

      // Apply or clear enablePrintReference
      if (enablePrintRefKey && settings[enablePrintRefKey]) {
        const value = settings[enablePrintRefKey] === "true";
        formChange("enablePrintReference", value);
      } else {
        formChange("enablePrintReference", undefined);
      }

      // Apply or clear hasConfidenceLevels
      if (confidenceLevelsKey && settings[confidenceLevelsKey]) {
        const value = settings[confidenceLevelsKey] === "true";
        formChange("hasConfidenceLevels", value);
      } else {
        formChange("hasConfidenceLevels", undefined);
      }

      // Apply or clear passingScore
      if (passingScoreKey && settings[passingScoreKey]) {
        const score = parseFloat(settings[passingScoreKey]);
        if (!isNaN(score)) {
          formChange("passingScore", score);
        } else {
          formChange("passingScore", undefined);
        }
      } else {
        formChange("passingScore", undefined);
      }

      // Apply or clear attemptsAllowed
      if (attemptsAllowedKey && settings[attemptsAllowedKey]) {
        const attempts = parseInt(settings[attemptsAllowedKey], 10);
        if (!isNaN(attempts)) {
          formChange("attemptsAllowed", attempts);
        } else {
          formChange("attemptsAllowed", undefined);
        }
      } else {
        formChange("attemptsAllowed", undefined);
      }
    } else if (settingGroupId) {
      // If we have a settingGroupId but no settings data yet (loading state), clear the fields
      formChange("enablePrintReference", undefined);
      formChange("hasConfidenceLevels", undefined);
      formChange("passingScore", undefined);
      formChange("attemptsAllowed", undefined);
    }
  }, [settingsDict, settingGroupId, formChange, mode]);

  return null;
};

export default DefaultOrganizationEvaluationSettingsPopulator;
